import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { ArrowRight, FileText, FolderOpen } from 'lucide-react';
import * as React from 'react';

import { Dialog, DialogPanel } from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import type { ActionBarAction } from '~/lib/action-bar';
import { api, type SearchResponse } from '~/lib/api';
import { cn } from '~/lib/utils';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  actions: ActionBarAction[];
  hideSearchResults?: boolean;
  onActionSelected?: () => void;
};

function normalize(s: string) {
  return s.trim().toLowerCase();
}

function actionText(a: ActionBarAction) {
  return [a.label, ...(a.keywords ?? [])].join(' ').toLowerCase();
}

export function ActionBarDialog({
  open,
  onOpenChange,
  actions,
  hideSearchResults = false,
  onActionSelected
}: Props) {
  const router = useRouter();
  const [query, setQuery] = React.useState('');
  const [debouncedQuery, setDebouncedQuery] = React.useState('');
  const [activeIndex, setActiveIndex] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [query]);

  const { data: searchData } = useQuery({
    queryKey: ['action-bar-search', debouncedQuery, hideSearchResults],
    queryFn: () =>
      api.get<SearchResponse>(
        `/api/search?q=${encodeURIComponent(debouncedQuery)}&limit=20`
      ),
    enabled: !hideSearchResults && debouncedQuery.trim().length >= 2,
    staleTime: 30_000
  });

  const searchActions = React.useMemo<ActionBarAction[]>(() => {
    if (hideSearchResults) return [];
    if (!searchData?.results.length) return [];
    return searchData.results
      .filter((r) => r.type === 'file' || r.type === 'folder')
      .map((r) => ({
        id: `search.${r.type}.${r.id}`,
        label: r.name,
        group: 'Search results',
        keywords: [r.type, r.roomId ?? ''],
        icon: r.type === 'file' ? FileText : FolderOpen,
        onSelect: () => {
          if (r.type === 'file' && r.roomId) {
            void router.navigate({
              to: '/rooms/$roomId/files/$fileId',
              params: { roomId: r.roomId, fileId: r.id }
            });
          } else if (r.type === 'folder' && r.roomId) {
            void router.navigate({
              to: '/rooms/$roomId',
              params: { roomId: r.roomId },
              search: { folderId: r.id }
            });
          }
        }
      }));
  }, [hideSearchResults, searchData, router]);

  const filtered = React.useMemo(() => {
    const q = normalize(query);
    const enabled = actions.filter((a) => a.enabled !== false);
    const staticFiltered = !q
      ? enabled
      : enabled.filter((a) => actionText(a).includes(q));
    return [...staticFiltered, ...searchActions];
  }, [actions, query, searchActions]);

  const indices = React.useMemo(() => {
    const map = new Map<string, number>();
    for (let i = 0; i < filtered.length; i++) map.set(filtered[i]!.id, i);
    return map;
  }, [filtered]);

  const groups = React.useMemo(() => {
    const map = new Map<string, ActionBarAction[]>();
    for (const a of filtered) {
      const key = a.group ?? 'Actions';
      const arr = map.get(key);
      if (arr) arr.push(a);
      else map.set(key, [a]);
    }
    return [...map.entries()];
  }, [filtered]);

  const flat = filtered;

  React.useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIndex(0);
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  React.useEffect(() => {
    if (activeIndex < 0) setActiveIndex(0);
    if (activeIndex >= flat.length)
      setActiveIndex(Math.max(0, flat.length - 1));
  }, [activeIndex, flat.length]);

  const selectAction = React.useCallback(
    async (action: ActionBarAction | undefined) => {
      if (!action) return;
      await action.onSelect();
      onActionSelected?.();
      onOpenChange(false);
    },
    [onActionSelected, onOpenChange]
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(flat.length - 1, i + 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      void selectAction(flat[activeIndex]);
      return;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPanel className="max-w-xl p-0">
        <div className="border-b p-3">
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type a command or search…"
            onKeyDown={onKeyDown}
            className="h-11"
          />
          <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
            <span>
              Use <span className="font-medium">↑</span>/
              <span className="font-medium">↓</span> and{' '}
              <span className="font-medium">Enter</span>
            </span>
            <span className="hidden sm:inline">Esc to close</span>
          </div>
        </div>

        <div className="max-h-[min(60vh,32rem)] overflow-y-auto p-2">
          {flat.length === 0 ? (
            <div className="px-3 py-8 text-center text-sm text-muted-foreground">
              No matches.
            </div>
          ) : (
            <div className="space-y-2">
              {groups.map(([groupName, groupActions]) => (
                <div key={groupName}>
                  <div className="px-2 py-1 text-[0.7rem] font-semibold uppercase tracking-wider text-muted-foreground">
                    {groupName}
                  </div>
                  <div className="space-y-1">
                    {groupActions.map((a) => {
                      const idx = indices.get(a.id) ?? 0;
                      const active = idx === activeIndex;
                      const Icon = a.icon;
                      return (
                        <button
                          key={a.id}
                          type="button"
                          className={cn(
                            'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm outline-none transition-colors',
                            active
                              ? 'bg-accent text-accent-foreground'
                              : 'hover:bg-accent/60 text-foreground'
                          )}
                          onMouseEnter={() => setActiveIndex(idx)}
                          onClick={() => void selectAction(a)}
                        >
                          <span className="flex h-8 w-8 items-center justify-center rounded-md border bg-background">
                            {Icon ? (
                              <Icon className="h-4 w-4 opacity-80" />
                            ) : (
                              <ArrowRight className="h-4 w-4 opacity-70" />
                            )}
                          </span>
                          <span className="min-w-0 flex-1 truncate">
                            {a.label}
                          </span>
                          <ArrowRight
                            className={cn(
                              'h-4 w-4 opacity-0 transition-opacity',
                              active && 'opacity-70'
                            )}
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogPanel>
    </Dialog>
  );
}
